import React from "react";
import {useIdleTimer} from "react-idle-timer";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import {FormButton} from "components/FormComponents/Button/FormButton";
import FormInstructions from "components/FormComponents/Typography/FormInstructions";
import {useAppSelector, useAppDispatch, store} from "../../redux/store";
import {getRequest, setHeader} from "repository/genericRepository";
import {FETCH_TOKEN} from "commonUtils/globalAPISettings";
import {clearAuthState} from "../../redux/reducers/authSlice";
import {useNavigate} from "react-router-dom";
import {clearlogs, postLogsToAPI} from "../../redux/reducers/loggerSlice";
import DialogTitleCommon from "components/Dialogs/DialogTitleCommon";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Grid from "@mui/material/Grid";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  CPSConstants,
  PageNames,
} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {logMessage} from "services/loggerService";
import {
  handleAxiosErrors,
  initialMessage,
  loggedMessage,
  message,
} from "commonUtils/utils";

const IdleTimerContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const IDLE_TIMEOUT_SECONDS = 1020;
  const COUNTDOWN_SECONDS = 179;

  const [open, setOpen] = React.useState(false);
  const [countdown, setCountdown] = React.useState(COUNTDOWN_SECONDS);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  const expiryTimestampRef = React.useRef<number | null>(null);

  const tickIntervalRef = React.useRef<number | null>(null);

  const suppressIdleUntilRef = React.useRef<number>(0);

  const {authDetails, userDetails} = useAppSelector(state => state.auth);
  const logs = useAppSelector(state => state.logs);

  const getTokenCall = async () => {
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const responseData = await getRequest({
        url: `${FETCH_TOKEN}`,
        headers: setHeader(APIHeaders),
        timeout: API_TIMEOUTS_MILLISECONDS.FETCH_TOKEN,
        params: {},
      });
      if (responseData?.messageCode !== 0 && responseData?.status === false) {
        dispatch(clearAuthState());
        navigate("/");
      }
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  };

  const forceLogout = React.useCallback(async () => {
    try {
      dispatch(postLogsToAPI({logs, token: authDetails?.token}));
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    } finally {
      dispatch(clearlogs());
      dispatch(clearAuthState());
      setOpen(false);
      expiryTimestampRef.current = null;
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      setCountdown(COUNTDOWN_SECONDS);
      navigate(CPSConstants.UNAUTH_QUERY_STRING_SESSION);
    }
  }, [dispatch, logs, authDetails?.token, navigate]);

  const {reset} = useIdleTimer({
    onIdle: () => {
      if (Date.now() < suppressIdleUntilRef.current) return;

      try {
        dispatch(postLogsToAPI({logs, token: authDetails?.token}));
        dispatch(clearlogs());
      } catch (error: any) {
        handleAxiosErrors(error, setShowAlert, setServerValidation);
      }
      setOpen(true);
      expiryTimestampRef.current = Date.now() + COUNTDOWN_SECONDS * 1000;

      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
      tickIntervalRef.current = window.setInterval(() => {
        if (!expiryTimestampRef.current) return;
        const msLeft = expiryTimestampRef.current - Date.now();
        const secsLeft = Math.max(Math.ceil(msLeft / 1000), 0);
        setCountdown(secsLeft);

        if (msLeft <= 0) {
          clearInterval(tickIntervalRef.current!);
          tickIntervalRef.current = null;
          forceLogout();
        }
      }, 500) as unknown as number;
    },
    timeout: IDLE_TIMEOUT_SECONDS * 1000,
    eventsThrottle: 200,
    stopOnIdle: false,
    debounce: 250,
  });

  React.useEffect(() => {
    const checkExpiry = () => {
      if (!open || !expiryTimestampRef.current) return;
      if (Date.now() >= expiryTimestampRef.current) {
        forceLogout();
      } else {
        const msLeft = expiryTimestampRef.current - Date.now();
        setCountdown(Math.max(Math.ceil(msLeft / 1000), 0));
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkExpiry();
    };

    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", checkExpiry);

    return () => {
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", checkExpiry);
    };
  }, [open, forceLogout]);

  const handleSignin = async () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} clicked "Yes, Keep me signed in".`,
        PageNames.DASHBOARD,
      );

      suppressIdleUntilRef.current = Date.now() + 3000;

      setOpen(false);
      expiryTimestampRef.current = null;
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      setCountdown(COUNTDOWN_SECONDS);

      reset();

      await getTokenCall();
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  };

  const handleSignOut = async () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} clicked "No, Sign me out".`,
        PageNames.DASHBOARD,
      );
      await forceLogout();
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  };

  React.useEffect(() => {
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            maxHeight: "25rem",
            height: "18rem",
            width: "30rem",
            padding: "0 0 10px 0",
          },
        }}>
        <DialogTitleCommon title="Confirm" />
        <DialogContent>
          {showAlert && serverValidation?.message && (
            <FormValidationAlert
              closeIcon={true}
              showAlertBox={showAlert}
              setShowAlertBox={setShowAlert}
              severity={
                (serverValidation?.isSuccessMessage ?? false)
                  ? Alerts.SUCCESS
                  : (serverValidation?.isValidationMessage ?? false)
                    ? Alerts.INFO
                    : Alerts.ERROR
              }
              message={serverValidation?.message}
            />
          )}
          <DialogContentText id="alert-dialog-description">
            <br />
            <Grid container spacing={1}>
              <Grid item xs={1}>
                <WarningAmberIcon sx={{color: "red", fontSize: "4rem"}} />
              </Grid>
              <Grid item xs={11}>
                <FormInstructions
                  outlet={`You will be logged out in ${countdown} seconds.`}
                />
                <br />
                <FormInstructions outlet="Do you want to stay signed in?" />
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{display: "flex", justifyContent: "center"}}>
          <FormButton
            size={"small"}
            variant="contained"
            onClick={handleSignOut}
            outlet="No, Sign me out"
          />
          <FormButton
            size={"small"}
            variant="contained"
            onClick={handleSignin}
            autoFocus
            outlet="Yes, Keep me signed in"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IdleTimerContainer;
